import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Cancel, CheckCircle, ExpandMore, LocationCity, Public } from '@mui/icons-material';
import { AgencyIcon } from '../AgencyIcon';

import { BRAZILIAN_STATES } from './brazilianStates';
import { AgencySelectableList } from './AgencySelectableList';
import { TodasMasterSwitch } from './TodasMasterSwitch';
import { useAgencyGroups } from './useAgencyGroups';
import type { AgencyListMode, AgencyRow } from './types';

type Props = {
  agencies: AgencyRow[];
  mode: AgencyListMode;
  selectedNames?: string[];
  onToggleAgency: (code: string, name: string) => void;
  isToggleLoading?: boolean;
  onViewHistory?: (agency: AgencyRow) => void;
  onViewLegislations?: (agency: AgencyRow) => void;
  showTodasMaster?: boolean;
  selectAllMode?: boolean;
  onMasterChange?: (all: boolean) => void;
};

export function AgencyMonitoringBrowsePanel({
  agencies,
  mode,
  selectedNames = [],
  onToggleAgency,
  isToggleLoading,
  onViewHistory,
  onViewLegislations,
  showTodasMaster,
  selectAllMode = false,
  onMasterChange,
}: Props) {
  const [selectedCountry, setSelectedCountry] = useState('brasil');
  const [tabValue, setTabValue] = useState(0);
  const [selectedState, setSelectedState] = useState('');

  const { federalAgencies, stateAgencies, stateAgenciesByState } = useAgencyGroups(agencies);

  const totalActive =
    mode === 'monitoring'
      ? agencies.filter((a) => a.isMonitored).length
      : selectAllMode
        ? agencies.length
        : selectedNames.length;

  const federalActive =
    mode === 'monitoring'
      ? federalAgencies.filter((a) => a.isMonitored).length
      : selectAllMode
        ? federalAgencies.length
        : federalAgencies.filter((a) => selectedNames.includes(a.name)).length;

  const stateActive =
    mode === 'monitoring'
      ? stateAgencies.filter((a) => a.isMonitored).length
      : selectAllMode
        ? stateAgencies.length
        : stateAgencies.filter((a) => selectedNames.includes(a.name)).length;

  const summaryVerb = mode === 'monitoring' ? 'sendo monitoradas' : 'selecionadas para o dashboard';

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="country-select-label">País</InputLabel>
          <Select
            labelId="country-select-label"
            value={selectedCountry}
            label="País"
            onChange={(e) => setSelectedCountry(e.target.value)}
            startAdornment={<Public sx={{ mr: 1, color: 'action.active' }} />}
          >
            <MenuItem value="brasil">Brasil</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Alert severity="info" icon={<CheckCircle />}>
          <Typography variant="body2">
            {selectAllMode && mode === 'selection' ? (
              <>
                <strong>Todas</strong> as <strong>{agencies.length}</strong> fontes do catálogo ({federalAgencies.length}{' '}
                federais, {stateAgencies.length} estaduais) — sem filtro no dashboard
              </>
            ) : (
              <>
                <strong>{totalActive}</strong> de <strong>{agencies.length}</strong> agências {summaryVerb} (
                {federalActive} federais, {stateActive} estaduais)
              </>
            )}
          </Typography>
        </Alert>
      </Box>

      {showTodasMaster && onMasterChange && (
        <Box sx={{ mb: 2 }}>
          <TodasMasterSwitch
            checked={selectAllMode}
            onChange={onMasterChange}
            description="Sem filtro — o dashboard considera todas as fontes do catálogo"
          />
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab
            icon={<AgencyIcon />}
            iconPosition="start"
            label={`Agências Federais (${federalAgencies.length})`}
          />
          <Tab
            icon={<LocationCity />}
            iconPosition="start"
            label={`Agências Estaduais (${stateAgencies.length})`}
          />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <AgencySelectableList
          agencies={federalAgencies}
          mode={mode}
          selectedNames={selectedNames}
          selectAllMode={selectAllMode}
          onToggle={onToggleAgency}
          onViewHistory={onViewHistory}
          onViewLegislations={onViewLegislations}
          isLoading={isToggleLoading}
          emptyMessage="Nenhuma agência federal disponível"
          chipColor="primary"
        />
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="state-select-label">Selecione o Estado</InputLabel>
              <Select
                labelId="state-select-label"
                value={selectedState}
                label="Selecione o Estado"
                onChange={(e) => setSelectedState(e.target.value)}
                startAdornment={<LocationCity sx={{ mr: 1, color: 'action.active' }} />}
              >
                <MenuItem value="">
                  <em>Todos os Estados</em>
                </MenuItem>
                {BRAZILIAN_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.name} ({state.code.toUpperCase()})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedState ? (
            stateAgenciesByState[selectedState]?.length > 0 ? (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{stateAgenciesByState[selectedState].length}</strong> agência(s) para{' '}
                    <strong>{BRAZILIAN_STATES.find((s) => s.code === selectedState)?.name}</strong>
                  </Typography>
                </Alert>
                <AgencySelectableList
                  agencies={stateAgenciesByState[selectedState]}
                  mode={mode}
                  selectedNames={selectedNames}
                  selectAllMode={selectAllMode}
                  onToggle={onToggleAgency}
                  onViewHistory={onViewHistory}
                  onViewLegislations={onViewLegislations}
                  isLoading={isToggleLoading}
                  chipColor="secondary"
                />
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                <Cancel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Nenhuma agência disponível para este estado
                </Typography>
              </Box>
            )
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Selecione um estado acima ou expanda os acordeões abaixo
                </Typography>
              </Alert>
              {BRAZILIAN_STATES.filter(
                (state) => stateAgenciesByState[state.code]?.length > 0,
              ).map((state) => (
                <Accordion key={state.code} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" gap={2} width="100%">
                      <LocationCity color="action" />
                      <Typography fontWeight={600}>{state.name}</Typography>
                      <Chip
                        label={`${stateAgenciesByState[state.code].length} agência(s)`}
                        size="small"
                      />
                      <Chip
                        label={`${
                          mode === 'monitoring'
                            ? stateAgenciesByState[state.code].filter((a) => a.isMonitored).length
                            : selectAllMode
                              ? stateAgenciesByState[state.code].length
                              : stateAgenciesByState[state.code].filter((a) =>
                                  selectedNames.includes(a.name),
                                ).length
                        } ativa(s)`}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <AgencySelectableList
                      agencies={stateAgenciesByState[state.code]}
                      mode={mode}
                      selectedNames={selectedNames}
                      selectAllMode={selectAllMode}
                      onToggle={onToggleAgency}
                      onViewHistory={onViewHistory}
                      onViewLegislations={onViewLegislations}
                      isLoading={isToggleLoading}
                      chipColor="secondary"
                    />
                  </AccordionDetails>
                </Accordion>
              ))}

              {Object.keys(stateAgenciesByState).length === 0 && (
                <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                  <Cancel sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Nenhuma agência estadual disponível
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
}
